import { useCallback, useEffect, useRef, useState } from 'react';
import {
  validateInitiativeRequestSchema,
  validateInitiativeResponseSchema,
  type InitiativeWriteInput,
} from '@complan/contracts';
import { apiFetch } from '@/shared/api/client';
import { fromServerFinding, type SummaryFinding } from './validation-model';

/**
 * Server-authoritative validation for the intake form, debounced at 300 ms
 * against the 1-second freshness target of R5.7 (task 23.2).
 *
 * The Validation_Engine on the server is the authority (R5.7, R8.12): it returns
 * the complete finding set of all four tiers in one pass. This hook re-runs that
 * evaluation on committed field changes, but only after a 300 ms quiet period, so
 * a burst of keystrokes collapses to one round trip and the summary still updates
 * comfortably inside the 1-second bound.
 *
 * `revalidate(values)` is called by the wizard whenever a field is committed. It
 * schedules a debounced `POST /api/initiatives(/:id)/validate`; the latest call
 * wins and any in-flight response for a superseded call is ignored (a monotonic
 * request sequence guards against out-of-order resolution). The result carries the
 * adapted server findings and the set of warning rule ids still awaiting
 * acknowledgement.
 *
 * ## Degradation
 *
 * `POST /api/initiatives(/:id)/validate` is registered on the API
 * (`initiative-validation-routes.ts`). If a call fails for any reason this hook
 * keeps `serverEvaluated: false`, so the summary falls back to the client-side
 * syntactic hard rules alone and the form stays usable.
 *
 * Warning acknowledgement does not round-trip yet:
 * {@link validateInitiativeResponseSchema} carries no witness hashes, and
 * acknowledging a warning requires the client to send the hash of the value the
 * warning was raised against. The response reports which warnings are
 * unacknowledged; confirming one needs a contract change.
 */

/** The debounce window R5.7's re-validation runs behind (task 23.2). */
export const VALIDATE_DEBOUNCE_MS = 300;

export interface ServerValidationState {
  /** The adapted server findings of the most recent successful evaluation. */
  readonly findings: readonly SummaryFinding[];
  /** Warning rule ids of that evaluation still needing acknowledgement (R8.5). */
  readonly unacknowledgedWarningRuleIds: readonly string[];
  /** True once at least one server evaluation has resolved; gates the merge. */
  readonly serverEvaluated: boolean;
  /** True while a debounced evaluation is scheduled or in flight. */
  readonly isRevalidating: boolean;
}

const INITIAL_STATE: ServerValidationState = {
  findings: [],
  unacknowledgedWarningRuleIds: [],
  serverEvaluated: false,
  isRevalidating: false,
};

/**
 * Drop the form's "not yet provided" placeholders so an incomplete form can be
 * validated at all.
 *
 * `validateInitiativeRequestSchema` makes every field optional precisely because a
 * form under edit is incomplete, but optional means *absent*: a field that is
 * present still has to satisfy its own constraint. `emptyIntakeValues` seeds
 * unfilled controls with `''` (and `platforms` with `[]`, the one array the write
 * shape requires a member of), so sending the raw form state makes the request
 * schema reject the body before it ever reaches the network — the call silently
 * never happens and the summary claims the initiative is ready to submit while
 * whole steps are untouched. Omitting the placeholders states the truth the server
 * is built to answer: the field has not been provided yet.
 *
 * Only the unset markers are pruned. An empty `channelIds`/`audienceIds` is a real
 * answer and those arrays default to `[]` in the
 * write shape, so they are sent as they stand.
 */
function pruneUnsetValues(values: Partial<InitiativeWriteInput>): Partial<InitiativeWriteInput> {
  const pruned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value === '') continue;
    if (key === 'platforms' && Array.isArray(value) && value.length === 0) continue;
    pruned[key] = value;
  }
  return pruned as Partial<InitiativeWriteInput>;
}

/** The path the validate call posts to: record-scoped on edit, collection on create. */
function validatePath(initiativeId: string | undefined): string {
  return initiativeId ? `/api/initiatives/${initiativeId}/validate` : '/api/initiatives/validate';
}

export interface UseValidateInitiativeResult extends ServerValidationState {
  /** Schedule a debounced server re-validation for the given (partial) form values. */
  readonly revalidate: (values: Partial<InitiativeWriteInput>) => void;
}

export function useValidateInitiative(
  initiativeId?: string,
  loadedVersion?: number | null,
): UseValidateInitiativeResult {
  const [state, setState] = useState<ServerValidationState>(INITIAL_STATE);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic sequence so a slow earlier response cannot overwrite a later one.
  const sequenceRef = useRef(0);

  const runValidation = useCallback(
    async (values: Partial<InitiativeWriteInput>, sequence: number): Promise<void> => {
      try {
        const body = validateInitiativeRequestSchema.parse({
          version: loadedVersion ?? null,
          initiative: pruneUnsetValues(values),
        });
        const raw = await apiFetch<unknown>(validatePath(initiativeId), {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (sequence !== sequenceRef.current) return; // superseded
        const parsed = validateInitiativeResponseSchema.parse(raw);
        setState({
          findings: parsed.findings.map(fromServerFinding),
          unacknowledgedWarningRuleIds: parsed.unacknowledgedWarningRuleIds,
          serverEvaluated: true,
          isRevalidating: false,
        });
      } catch {
        if (sequence !== sequenceRef.current) return;
        // Route not yet available (or a transient failure): keep the last known
        // server state but stop showing the spinner. serverEvaluated stays false
        // if we never got a good answer, so the summary uses client rules alone.
        setState((current) => ({ ...current, isRevalidating: false }));
      }
    },
    [initiativeId, loadedVersion],
  );

  const revalidate = useCallback(
    (values: Partial<InitiativeWriteInput>): void => {
      if (timerRef.current) clearTimeout(timerRef.current);
      setState((current) => ({ ...current, isRevalidating: true }));
      const sequence = ++sequenceRef.current;
      timerRef.current = setTimeout(() => {
        void runValidation(values, sequence);
      }, VALIDATE_DEBOUNCE_MS);
    },
    [runValidation],
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return { ...state, revalidate };
}
